import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { PROCESS_CATEGORY_LABEL, type ProcessCategory } from "./api";

/** 1 ページの件数。 */
export const PROCESSES_PAGE_SIZE = 50;

/** 使用状態の絞り込み。`all` は使用中・停止中の両方。 */
export type ProcessUsageFilter = "all" | "active" | "stopped";

/** 費目区分の絞り込み。`all` は全区分。 */
export type ProcessCategoryFilter = "all" | ProcessCategory;

export interface ProcessListParamsState {
  page: number;
  keyword: string;
  usage: ProcessUsageFilter;
  category: ProcessCategoryFilter;
  sortKey: string;
  ascending: boolean;
}

const DEFAULTS: ProcessListParamsState = {
  page: 1,
  keyword: "",
  // マスタ台帳なので既定は全件（停止中も見える）。見積の工程候補を出す場面は
  // 別途 disabled=false で引く。
  usage: "all",
  category: "all",
  sortKey: "process_code",
  ascending: true,
};

/**
 * 工程一覧の検索条件・並び替え・ページを URL のクエリに持つ。
 *
 * 取引先一覧（[[usePartnerListParams]]）と同じ方針。URL に置くのはリロードで
 * 復元されるだけでなく、URL を共有でき、ブラウザの戻る/進むが効くため。
 */
export function useProcessListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<ProcessListParamsState>(() => {
    const page = Number(searchParams.get("page") ?? DEFAULTS.page);
    const usage = searchParams.get("usage");
    const category = searchParams.get("category");
    return {
      page: Number.isFinite(page) && page >= 1 ? page : DEFAULTS.page,
      keyword: searchParams.get("keyword") ?? DEFAULTS.keyword,
      usage: usage === "active" || usage === "stopped" ? usage : DEFAULTS.usage,
      // 未知の区分は無視して全区分に戻す（API に渡すと 422 になるため）。
      category:
        category && category in PROCESS_CATEGORY_LABEL
          ? (category as ProcessCategory)
          : DEFAULTS.category,
      sortKey: searchParams.get("sort_key") ?? DEFAULTS.sortKey,
      ascending: (searchParams.get("ascending") ?? "true") === "true",
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<ProcessListParamsState>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...params, ...patch };
          // 条件を変えたらページを 1 に戻す（page 自体の変更を除く）。
          if (patch.page === undefined) merged.page = 1;

          const set = (key: string, value: string, fallback: string) =>
            value === fallback ? next.delete(key) : next.set(key, value);

          set("page", String(merged.page), String(DEFAULTS.page));
          set("keyword", merged.keyword, DEFAULTS.keyword);
          set("usage", merged.usage, DEFAULTS.usage);
          set("category", merged.category, DEFAULTS.category);
          set("sort_key", merged.sortKey, DEFAULTS.sortKey);
          set("ascending", String(merged.ascending), String(DEFAULTS.ascending));
          return next;
        },
        { replace: true },
      );
    },
    [params, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  /** API に渡す disabled。全件のときは未指定にする。 */
  const disabledParam = params.usage === "all" ? undefined : params.usage === "stopped";
  /** API に渡す category。全区分のときは未指定にする。 */
  const categoryParam = params.category === "all" ? undefined : params.category;

  return { params, update, reset, disabledParam, categoryParam };
}
